import React, { type FC, useCallback, useEffect, useRef, useState } from 'react';

import { Button, Progress, Select, TextInput } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import clx from 'classnames';
import { devicesApi } from 'client/api';
import { useFetchingHandler } from 'client/hooks/useFetchingHandler';
import { getMarketDeviceId, MarketDevice } from 'client/model/devices';
import { SmartHome } from 'client/model/skill';
import { DeviceLinkingError, DeviceLinkingServerError, getDeviceLinkingErrorMessage } from 'client/utils/market';
import { isNumeric } from 'client/utils/validation';
import { getErrorMessage } from 'common/utils/errorsHandler';
import * as keyset from 'i18n/Settings';
import { useDebouncedCallback } from 'use-debounce';

import styles from './DeviceLinking.module.scss';

const t = i18n(keyset);

interface DeviceLinkingInputProps {
    isDraft: boolean;
    aliceApp: SmartHome;
    devices: MarketDevice[];
    onDeviceAdd: (device: MarketDevice) => void;
    disabled?: boolean;
}

const DeviceLinkingSuggest: FC<{
    device: MarketDevice;
}> = ({ device }) => {
    const { picture, title, vendorName } = device;
    const deviceId = getMarketDeviceId(device);

    return (
        <div className={styles.deviceLinkingSuggest__container}>
            <div className={styles.deviceLinkingSuggest__imageContainer}>
                <img src={picture} />
            </div>
            <div>
                <div className={styles.deviceLinkingSuggest__text}>{title}</div>
                <div className={styles.deviceLinkingSuggest__subtitle}>
                    {`${vendorName ? `${vendorName} • ` : ''}  ID ${deviceId}`}
                </div>
            </div>
        </div>
    );
};

export const DeviceLinkingInput: FC<DeviceLinkingInputProps> = ({
    aliceApp,
    isDraft,
    disabled,
    onDeviceAdd,
    devices,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [inputText, setInputText] = useState('');
    const [errorCode, setErrorCode] = useState<DeviceLinkingError | undefined>(undefined);
    const [isSuggestDataFetching, setIsSuggestDataFetching] = useState(false);
    const [suggests, setSuggests] = useState<MarketDevice[]>([]);
    const inputTextRef = useRef('');

    useEffect(() => {
        inputTextRef.current = inputText;
    }, [inputText]);

    useEffect(() => {
        return () => devicesApi.clearSearchCache();
    }, []);

    const addDeviceHandler = useCallback(
        (deviceId: string) =>
            isDraft
                ? devicesApi.addDevice(aliceApp.id, deviceId, aliceApp.channel)
                : devicesApi.addPublishedDevice(aliceApp.id, deviceId, aliceApp.channel),
        [isDraft, aliceApp.id, aliceApp.channel],
    );

    const onLoad = useCallback((device: MarketDevice) => onDeviceAdd(device), [onDeviceAdd]);

    const onError = useCallback((error: Error) => {
        const errorMessage = getErrorMessage(error);

        if (errorMessage && errorMessage.includes(DeviceLinkingServerError.NOT_FOUND)) {
            setErrorCode(DeviceLinkingError.NOT_FOUND);
            return;
        }

        if (errorMessage && errorMessage.includes(DeviceLinkingServerError.INVALID_ID)) {
            setErrorCode(DeviceLinkingError.INVALID);
            return;
        }

        setErrorCode(DeviceLinkingError.UNKNOWN);
    }, []);

    const [addDeviceStateful, state] = useFetchingHandler({
        handler: addDeviceHandler,
        onLoad,
        onError,
    });

    const addDeviceIfNotDuplicate = async (deviceId: string) => {
        setErrorCode(undefined);

        if (devices.some((device) => getMarketDeviceId(device) === deviceId)) {
            setErrorCode(DeviceLinkingError.DUPLICATE);

            return false;
        }

        await addDeviceStateful(deviceId);

        return true;
    };

    const onAdd = async () => {
        if (!inputText) {
            return;
        }

        if (await addDeviceIfNotDuplicate(inputText)) {
            setInputText('');
        }
    };

    const searchDebounced = useDebouncedCallback(async (text: string) => {
        setIsSuggestDataFetching(true);
        try {
            const devices = await devicesApi.searchMarketDevices(text);
            setSuggests(devices);
        } catch (error) {
            console.error('@DeviceLinkingInput', error);
            setSuggests([]);
        } finally {
            setIsSuggestDataFetching(false);
        }
    }, 400);

    const onChange = async (value: string) => {
        setInputText(value);
        if (!value.trim()) {
            setSuggests([]);
            return;
        }

        setIsOpen(true);
        searchDebounced(value);
    };

    const isNumericInput = isNumeric(inputText);

    const onKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
        if (e.key === 'Enter' && isNumericInput) {
            e.stopPropagation();
            e.preventDefault();
            void onAdd();
        }
    };

    const clearError = () => setErrorCode(undefined);

    const errorMessage = getDeviceLinkingErrorMessage(errorCode);

    const isLoading = state === 'loading' || isSuggestDataFetching;

    const handleClose = () => {
        setIsOpen(false);
        setSuggests([]);
    };

    const handleUpdate = async (values: string[]) => {
        const deviceId = values[0];
        setIsOpen(false);
        if (deviceId && (await addDeviceIfNotDuplicate(deviceId))) {
            setInputText('');
        }
    };

    return (
        <div>
            <div className={styles.deviceLinkingInputContainer} onClick={clearError}>
                <Select
                    value={['']}
                    renderControl={({ ref }) => {
                        return (
                            <TextInput
                                ref={ref}
                                value={inputText}
                                onUpdate={onChange}
                                onKeyDown={onKeyDown}
                                size="xl"
                                disabled={disabled}
                            />
                        );
                    }}
                    onFocus={() => setIsOpen(true)}
                    onBlur={handleClose}
                    onUpdate={handleUpdate}
                    open={isOpen && !!suggests.length}
                    size="xl"
                    getOptionHeight={() => 64}
                    error={errorMessage}
                    disabled={disabled}
                    renderOption={(option) => {
                        return <DeviceLinkingSuggest device={option.data} />;
                    }}
                    width="max"
                    loading={isLoading}
                >
                    {suggests.map((el) => {
                        const optionValue = getMarketDeviceId(el);

                        return <Select.Option key={optionValue} value={optionValue} data={el} />;
                    })}
                </Select>
                {isNumericInput && (
                    <Button onClick={onAdd} disabled={disabled || state === 'loading'} size="xl">
                        {t('devices-add')}
                    </Button>
                )}
            </div>

            <div className={styles.deviceLoadingProgress}>
                <Progress
                    className={clx(!isLoading && styles.deviceLoadingProgress__hiddenBlock)}
                    value={100}
                    loading={isLoading}
                    theme="info"
                    size="xs"
                />
            </div>
        </div>
    );
};
